/**
 * Copyright (c) 2018-2023 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __assign, __awaiter, __extends, __generator } from "tslib";
import { List } from 'immutable';
import { UUID } from '../../mol-util';
import { StatefulPluginComponent } from '../component';
import { utf8ByteCount, utf8Write } from '../../mol-io/common/utf8';
import { Zip } from '../../mol-util/zip/zip';
import { readFromFile } from '../../mol-util/data-source';
import { objectForEach } from '../../mol-util/object';
import { PLUGIN_VERSION } from '../../mol-plugin/version';
import { canvasToBlob } from '../../mol-canvas3d/util';
export { PluginStateSnapshotManager };
var PluginStateSnapshotManager = /** @class */ (function (_super) {
    __extends(PluginStateSnapshotManager, _super);
    function PluginStateSnapshotManager(plugin) {
        var _this = _super.call(this, {
            current: void 0,
            entries: List(),
            isPlaying: false,
            nextSnapshotDelayInMs: PluginStateSnapshotManager.DefaultNextSnapshotDelayInMs
        }) || this;
        _this.plugin = plugin;
        _this.entryMap = new Map();
        _this.defaultSnapshotId = undefined;
        _this.events = {
            changed: _this.ev(),
            opened: _this.ev(),
        };
        _this.timeoutHandle = void 0;
        _this.next = function () { return __awaiter(_this, void 0, void 0, function () {
            var next, snapshot, delay;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.timeoutHandle = void 0;
                        next = this.getNextId(this.state.current, 1);
                        if (!next || next === this.state.current) {
                            this.stop();
                            return [2 /*return*/];
                        }
                        snapshot = this.setCurrent(next);
                        return [4 /*yield*/, this.plugin.state.setSnapshot(snapshot)];
                    case 1:
                        _a.sent();
                        delay = typeof snapshot.durationInMs !== 'undefined' ? snapshot.durationInMs : this.state.nextSnapshotDelayInMs;
                        if (this.state.isPlaying)
                            this.timeoutHandle = setTimeout(this.next, delay);
                        return [2 /*return*/];
                }
            });
        }); };
        return _this;
        // TODO make nextSnapshotDelayInMs editable
    }
    PluginStateSnapshotManager.prototype.getIndex = function (e) {
        return this.state.entries.indexOf(e);
    };
    PluginStateSnapshotManager.prototype.getEntry = function (id) {
        if (!id)
            return;
        return this.entryMap.get(id);
    };
    PluginStateSnapshotManager.prototype.remove = function (id) {
        var e = this.entryMap.get(id);
        if (!e)
            return;
        if (e === null || e === void 0 ? void 0 : e.image)
            this.plugin.managers.asset.delete(e.image);
        this.entryMap.delete(id);
        this.updateState({
            current: this.state.current === id ? void 0 : this.state.current,
            entries: this.state.entries.delete(this.getIndex(e))
        });
        this.events.changed.next(void 0);
    };
    PluginStateSnapshotManager.prototype.add = function (e) {
        this.entryMap.set(e.snapshot.id, e);
        this.updateState({ current: e.snapshot.id, entries: this.state.entries.push(e) });
        this.events.changed.next(void 0);
    };
    PluginStateSnapshotManager.prototype.replace = function (id, snapshot, params) {
        var _a, _b, _c;
        var old = this.getEntry(id);
        if (!old)
            return;
        this.defaultSnapshotId = undefined;
        if (old === null || old === void 0 ? void 0 : old.image)
            this.plugin.managers.asset.delete(old.image);
        var idx = this.getIndex(old);
        // The id changes here!
        var e = PluginStateSnapshotManager.Entry(snapshot, {
            key: (_a = params === null || params === void 0 ? void 0 : params.key) !== null && _a !== void 0 ? _a : old.key,
            name: (_b = params === null || params === void 0 ? void 0 : params.name) !== null && _b !== void 0 ? _b : old.name,
            description: (_c = params === null || params === void 0 ? void 0 : params.description) !== null && _c !== void 0 ? _c : old.description,
            image: params === null || params === void 0 ? void 0 : params.image,
        });
        this.entryMap.set(snapshot.id, e);
        this.updateState({ current: e.snapshot.id, entries: this.state.entries.set(idx, e) });
        this.events.changed.next(void 0);
    };
    PluginStateSnapshotManager.prototype.move = function (id, dir) {
        var len = this.state.entries.size;
        if (len < 2)
            return;
        var e = this.getEntry(id);
        if (!e)
            return;
        var from = this.getIndex(e);
        var to = (from + dir) % len;
        if (to < 0)
            to += len;
        var f = this.state.entries.get(to);
        var entries = this.state.entries.asMutable();
        entries.set(to, e);
        entries.set(from, f);
        this.updateState({ current: e.snapshot.id, entries: entries.asImmutable() });
        this.events.changed.next(void 0);
    };
    PluginStateSnapshotManager.prototype.update = function (e, options) {
        var _a, _b, _c;
        var idx = this.getIndex(e);
        if (idx < 0)
            return;
        var entries = this.state.entries.set(idx, __assign(__assign({}, e), { key: ((_a = options.key) === null || _a === void 0 ? void 0 : _a.trim()) || undefined, name: ((_b = options.name) === null || _b === void 0 ? void 0 : _b.trim()) || undefined, description: ((_c = options.description) === null || _c === void 0 ? void 0 : _c.trim()) || undefined }));
        this.updateState({ entries: entries });
        this.entryMap.set(e.snapshot.id, this.state.entries.get(idx));
        this.events.changed.next(void 0);
    };
    PluginStateSnapshotManager.prototype.clear = function () {
        var _this = this;
        if (this.state.entries.size === 0)
            return;
        this.entryMap.forEach(function (e) {
            if (e === null || e === void 0 ? void 0 : e.image)
                _this.plugin.managers.asset.delete(e.image);
        });
        this.entryMap.clear();
        this.updateState({ current: void 0, entries: List() });
        this.events.changed.next(void 0);
    };
    PluginStateSnapshotManager.prototype.applyKey = function (key) {
        var e = this.state.entries.find(function (e) { return e.key === key; });
        if (!e)
            return;
        this.updateState({ current: e.snapshot.id });
        this.events.changed.next(void 0);
        this.plugin.state.setSnapshot(e.snapshot);
    };
    PluginStateSnapshotManager.prototype.setCurrent = function (id) {
        var e = this.getEntry(id);
        if (e) {
            this.updateState({ current: id });
            this.events.changed.next(void 0);
        }
        return e && e.snapshot;
    };
    PluginStateSnapshotManager.prototype.getNextId = function (id, dir) {
        var len = this.state.entries.size;
        if (!id) {
            if (len === 0)
                return void 0;
            var idx_1 = dir === -1 ? len - 1 : 0;
            return this.state.entries.get(idx_1).snapshot.id;
        }
        var e = this.getEntry(id);
        if (!e)
            return;
        var idx = this.getIndex(e);
        if (idx < 0)
            return;
        idx = (idx + dir) % len;
        if (idx < 0)
            idx += len;
        return this.state.entries.get(idx).snapshot.id;
    };
    PluginStateSnapshotManager.prototype.setStateSnapshot = function (snapshot) {
        return __awaiter(this, void 0, void 0, function () {
            var entries, _i, _a, e, current, entry, next;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (snapshot.version !== PLUGIN_VERSION) {
                            // TODO
                            // console.warn('state snapshot version mismatch');
                        }
                        this.clear();
                        entries = List().asMutable();
                        for (_i = 0, _a = snapshot.entries; _i < _a.length; _i++) {
                            e = _a[_i];
                            this.entryMap.set(e.snapshot.id, e);
                            entries.push(e);
                        }
                        current = snapshot.current
                            ? snapshot.current
                            : snapshot.entries.length > 0
                                ? snapshot.entries[0].snapshot.id
                                : void 0;
                        this.updateState({
                            current: current,
                            entries: entries.asImmutable(),
                            isPlaying: false,
                            nextSnapshotDelayInMs: snapshot.playback ? snapshot.playback.nextSnapshotDelayInMs : PluginStateSnapshotManager.DefaultNextSnapshotDelayInMs
                        });
                        this.events.changed.next(void 0);
                        if (!current)
                            return [2 /*return*/];
                        entry = this.getEntry(current);
                        next = entry && entry.snapshot;
                        if (!next)
                            return [2 /*return*/];
                        return [4 /*yield*/, this.plugin.state.setSnapshot(next)];
                    case 1:
                        _b.sent();
                        if (snapshot.playback && snapshot.playback.isPlaying)
                            this.play(true);
                        return [2 /*return*/, next];
                }
            });
        });
    };
    PluginStateSnapshotManager.prototype.syncCurrent = function (options) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function () {
            var isEmpty, canReplace, snapshot, image, _c, current;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        isEmpty = this.state.entries.size === 0;
                        canReplace = this.state.entries.size === 1 && this.state.current && this.state.current === this.defaultSnapshotId;
                        if (!isEmpty && !canReplace)
                            return [2 /*return*/];
                        snapshot = this.plugin.state.getSnapshot(options === null || options === void 0 ? void 0 : options.params);
                        if (!((_b = (_a = options === null || options === void 0 ? void 0 : options.params) === null || _a === void 0 ? void 0 : _a.image) !== null && _b !== void 0 ? _b : this.plugin.state.snapshotParams.value.image)) return [3 /*break*/, 2];
                        return [4 /*yield*/, PluginStateSnapshotManager.getCanvasImageAsset(this.plugin, "".concat(snapshot.id, "-image.png"))];
                    case 1:
                        _c = _d.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        _c = undefined;
                        _d.label = 3;
                    case 3:
                        image = _c;
                        if (isEmpty) {
                            this.add(PluginStateSnapshotManager.Entry(snapshot, { name: options === null || options === void 0 ? void 0 : options.name, description: options === null || options === void 0 ? void 0 : options.description, image: image }));
                        }
                        else if (canReplace) {
                            current = this.getEntry(this.state.current);
                            if (current === null || current === void 0 ? void 0 : current.image)
                                this.plugin.managers.asset.delete(current.image);
                            this.replace(this.state.current, snapshot, { image: image });
                        }
                        this.defaultSnapshotId = snapshot.id;
                        return [2 /*return*/];
                }
            });
        });
    };
    PluginStateSnapshotManager.prototype.getStateSnapshot = function (options) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.syncCurrent(options)];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, {
                                timestamp: +new Date(),
                                version: PLUGIN_VERSION,
                                name: options && options.name,
                                description: options && options.description,
                                current: this.state.current,
                                playback: {
                                    isPlaying: !!(options && options.playOnLoad),
                                    nextSnapshotDelayInMs: this.state.nextSnapshotDelayInMs
                                },
                                entries: this.state.entries.valueSeq().toArray()
                            }];
                }
            });
        });
    };
    PluginStateSnapshotManager.prototype.serialize = function (options) {
        return __awaiter(this, void 0, void 0, function () {
            var json, _a, _b, state, zipDataObj, assets, _i, _c, _d, asset, file, _e, _f, _g, index, data, zipFile;
            return __generator(this, function (_h) {
                switch (_h.label) {
                    case 0:
                        _b = (_a = JSON).stringify;
                        return [4 /*yield*/, this.getStateSnapshot({ params: options === null || options === void 0 ? void 0 : options.params })];
                    case 1:
                        json = _b.apply(_a, [_h.sent(), null, 2]);
                        if (!(!(options === null || options === void 0 ? void 0 : options.type) || options.type === 'json' || options.type === 'molj')) return [3 /*break*/, 2];
                        return [2 /*return*/, new Blob([json], { type: 'application/json;charset=utf-8' })];
                    case 2:
                        state = new Uint8Array(utf8ByteCount(json));
                        utf8Write(state, 0, json);
                        zipDataObj = {
                            'state.json': state
                        };
                        assets = [];
                        _i = 0, _c = this.plugin.managers.asset.assets;
                        _h.label = 3;
                    case 3:
                        if (!(_i < _c.length)) return [3 /*break*/, 6];
                        _d = _c[_i], asset = _d.asset, file = _d.file;
                        assets.push([asset.id, asset]);
                        _e = zipDataObj;
                        _f = "assets/".concat(asset.id);
                        _g = Uint8Array.bind;
                        return [4 /*yield*/, file.arrayBuffer()];
                    case 4:
                        _e[_f] = new (_g.apply(Uint8Array, [void 0, _h.sent()]))();
                        _h.label = 5;
                    case 5:
                        _i++;
                        return [3 /*break*/, 3];
                    case 6:
                        if (assets.length > 0) {
                            index = JSON.stringify(assets, null, 2);
                            data = new Uint8Array(utf8ByteCount(index));
                            utf8Write(data, 0, index);
                            zipDataObj['assets.json'] = data;
                        }
                        return [4 /*yield*/, this.plugin.runTask(Zip(zipDataObj))];
                    case 7:
                        zipFile = _h.sent();
                        return [2 /*return*/, new Blob([zipFile], { type: 'application/zip' })];
                }
            });
        });
    };
    PluginStateSnapshotManager.prototype.open = function (file) {
        return __awaiter(this, void 0, void 0, function () {
            var fn, data, snapshot, data, assetData_1, stateFile, stateData, file_1, json, _a, _b, _i, json_1, _c, id, asset, snapshot, e_1;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        _d.trys.push([0, 15, , 16]);
                        fn = file.name.toLowerCase();
                        if (!(fn.endsWith('json') || fn.endsWith('molj'))) return [3 /*break*/, 8];
                        return [4 /*yield*/, this.plugin.runTask(readFromFile(file, 'string'))];
                    case 1:
                        data = _d.sent();
                        snapshot = JSON.parse(data);
                        if (!PluginStateSnapshotManager.isStateSnapshot(snapshot)) return [3 /*break*/, 3];
                        return [4 /*yield*/, this.setStateSnapshot(snapshot)];
                    case 2:
                        _d.sent();
                        return [3 /*break*/, 7];
                    case 3:
                        if (!PluginStateSnapshotManager.isStateSnapshot(snapshot.data)) return [3 /*break*/, 5];
                        return [4 /*yield*/, this.setStateSnapshot(snapshot.data)];
                    case 4:
                        _d.sent();
                        return [3 /*break*/, 7];
                    case 5: return [4 /*yield*/, this.plugin.state.setSnapshot(snapshot)];
                    case 6:
                        _d.sent();
                        _d.label = 7;
                    case 7: return [3 /*break*/, 14];
                    case 8: return [4 /*yield*/, this.plugin.runTask(readFromFile(file, 'zip'))];
                    case 9:
                        data = _d.sent();
                        assetData_1 = Object.create(null);
                        objectForEach(data, function (v, k) {
                            if (k === 'state.json' || k === 'assets.json')
                                return;
                            var name = k.substring(k.indexOf('/') + 1);
                            assetData_1[name] = v;
                        });
                        stateFile = new File([data['state.json']], 'state.json');
                        return [4 /*yield*/, this.plugin.runTask(readFromFile(stateFile, 'string'))];
                    case 10:
                        stateData = _d.sent();
                        if (!data['assets.json']) return [3 /*break*/, 12];
                        file_1 = new File([data['assets.json']], 'assets.json');
                        _b = (_a = JSON).parse;
                        return [4 /*yield*/, this.plugin.runTask(readFromFile(file_1, 'string'))];
                    case 11:
                        json = _b.apply(_a, [_d.sent()]);
                        for (_i = 0, json_1 = json; _i < json_1.length; _i++) {
                            _c = json_1[_i], id = _c[0], asset = _c[1];
                            this.plugin.managers.asset.set(asset, new File([assetData_1[id]], asset.name));
                        }
                        _d.label = 12;
                    case 12:
                        snapshot = JSON.parse(stateData);
                        return [4 /*yield*/, this.setStateSnapshot(snapshot)];
                    case 13:
                        _d.sent();
                        _d.label = 14;
                    case 14:
                        this.events.opened.next(void 0);
                        return [3 /*break*/, 16];
                    case 15:
                        e_1 = _d.sent();
                        console.error(e_1);
                        this.plugin.log.error('Error reading state');
                        return [3 /*break*/, 16];
                    case 16: return [2 /*return*/];
                }
            });
        });
    };
    PluginStateSnapshotManager.prototype.play = function (delayFirst) {
        if (delayFirst === void 0) { delayFirst = false; }
        this.updateState({ isPlaying: true });
        if (delayFirst) {
            var e = this.getEntry(this.state.current);
            if (!e) {
                this.next();
                return;
            }
            this.events.changed.next(void 0);
            var snapshot = e.snapshot;
            var delay = typeof snapshot.durationInMs !== 'undefined' ? snapshot.durationInMs : this.state.nextSnapshotDelayInMs;
            this.timeoutHandle = setTimeout(this.next, delay);
        }
        else {
            this.next();
        }
    };
    PluginStateSnapshotManager.prototype.stop = function () {
        this.updateState({ isPlaying: false });
        if (typeof this.timeoutHandle !== 'undefined')
            clearTimeout(this.timeoutHandle);
        this.timeoutHandle = void 0;
        this.events.changed.next(void 0);
    };
    PluginStateSnapshotManager.prototype.togglePlay = function () {
        if (this.state.isPlaying) {
            this.stop();
            this.plugin.managers.animation.stop();
        }
        else {
            this.play();
        }
    };
    PluginStateSnapshotManager.prototype.dispose = function () {
        _super.prototype.dispose.call(this);
        this.entryMap.clear();
    };
    PluginStateSnapshotManager.DefaultNextSnapshotDelayInMs = 1500;
    return PluginStateSnapshotManager;
}(StatefulPluginComponent));
(function (PluginStateSnapshotManager) {
    function Entry(snapshot, params) {
        return __assign({ timestamp: +new Date(), snapshot: snapshot }, params);
    }
    PluginStateSnapshotManager.Entry = Entry;
    function isStateSnapshot(x) {
        var s = x;
        return !!s && !!s.timestamp && !!s.entries;
    }
    PluginStateSnapshotManager.isStateSnapshot = isStateSnapshot;
    function getCanvasImageAsset(ctx, name) {
        return __awaiter(this, void 0, void 0, function () {
            var p, blob, file, image;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!ctx.helpers.viewportScreenshot)
                            return [2 /*return*/];
                        p = ctx.helpers.viewportScreenshot.getPreview(512);
                        if (!p)
                            return [2 /*return*/];
                        return [4 /*yield*/, canvasToBlob(p.canvas, 'png')];
                    case 1:
                        blob = _a.sent();
                        file = new File([blob], name);
                        image = { kind: 'file', id: UUID.create22(), name: name };
                        ctx.managers.asset.set(image, file);
                        return [2 /*return*/, image];
                }
            });
        });
    }
    PluginStateSnapshotManager.getCanvasImageAsset = getCanvasImageAsset;
})(PluginStateSnapshotManager || (PluginStateSnapshotManager = {}));
